"use client";

import clsx from "clsx";
import Icon, { ICON_ANIMATE } from "@/components/library/Icon";

interface StepButtonProps {
  /** Title text to display */
  title: string;
  /** Whether this step is completed */
  isCompleted: boolean;
  /** Whether this step is locked (future step) */
  isLocked: boolean;
  /** Whether the component is in a loading state */
  isLoading?: boolean;
  /** Click handler */
  onClick: () => void;
  /** Border radius variant */
  rounded?: "lg" | "2xl";
}

/**
 * A reusable step button component used in onboarding flows.
 * Shows different states: completed (checkmark), next (arrow), locked (circle), loading (spinner)
 */
export function StepButton({
  title,
  isCompleted,
  isLocked,
  isLoading = false,
  onClick,
  rounded = "lg",
}: Readonly<StepButtonProps>) {
  // Completed steps, locked steps, and loading state are all disabled
  const isDisabled = isLoading || isCompleted || isLocked;
  const isNextStep = !isCompleted && !isLocked;

  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={clsx(
        "w-full",
        "text-left",
        "px-4 py-3",
        "flex items-center justify-between",
        "border",
        "transition-colors",
        rounded === "lg" ? "rounded-lg" : "rounded-2xl",
        // Completed state - primary styling
        isCompleted && [
          "bg-primary/10",
          "border-primary/30",
          "cursor-default",
          "opacity-70",
        ],
        // Locked/future state - neutral styling
        isLocked && [
          "bg-panel_secondary",
          "border-neutral",
          "cursor-not-allowed",
          "opacity-70",
        ],
        // Next step (active) - clickable styling
        isNextStep && !isLoading && [
          "bg-primary/10",
          "border-primary/30",
          "hover:bg-primary/20",
        ],
        // Loading state
        isLoading && [
          "bg-panel_secondary",
          "border-neutral",
          "cursor-not-allowed",
          "opacity-70",
        ]
      )}
    >
      <h3 className={clsx("text-900", "flex", "items-center", "gap-2")}>
        {isLoading ? (
          <Icon icon="spinner" animate={ICON_ANIMATE.SPIN} className="w-4 h-4" />
        ) : isCompleted ? (
          <Icon icon="check" className="w-4 h-4 text-primary" />
        ) : isNextStep ? (
          <Icon icon="arrow-right" className="w-4 h-4 text-primary" />
        ) : (
          <Icon icon="circle" className="w-4 h-4" />
        )}
        {title}
      </h3>
      <Icon icon="chevron-right" className="w-4 h-4" />
    </button>
  );
}
